import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Edit2, Trash2, Eye, EyeOff, Search } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = { active: 'bg-green-100 text-green-800', draft: 'bg-yellow-100 text-yellow-800' };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const load = () => api.get('/admin/products').then(r => { setProducts(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggleStatus = async (p) => {
    try {
      await api.put(`/admin/products/${p.product_id}/toggle`);
      toast.success(`Product ${p.status === 'active' ? 'drafted' : 'published'}`);
      load();
    } catch { toast.error('Failed'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.product_id}`);
      toast.success('Product deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = products.filter(p => {
    const matchQ = !query || p.name?.toLowerCase().includes(query.toLowerCase());
    const matchC = !categoryFilter || p.category === categoryFilter;
    return matchQ && matchC;
  });

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  return (
    <div data-testid="admin-products-page" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif font-medium">Products</h1>
          <p className="text-sm text-muted-foreground">{products.length} total · manage independently</p>
        </div>
        <Link to="/admin/products/new">
          <Button data-testid="add-product-btn" className="rounded-full gap-2"><Plus className="h-3.5 w-3.5" /> Add New Product</Button>
        </Link>
      </div>

      <Card className="rounded-xl mb-4">
        <CardContent className="pt-4 pb-4 flex gap-3 items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="products-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search products..." className="pl-9" />
          </div>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="h-10 px-3 rounded-lg border border-input text-sm bg-background" data-testid="products-filter-category">
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </CardContent>
      </Card>

      <Card className="rounded-xl">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-10">No products yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Special</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Farmers</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.product_id} data-testid={`product-row-${p.product_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                        <div>
                          <p className="font-medium text-sm">{p.name}</p>
                          {p.variety && <p className="text-xs text-muted-foreground">{p.variety}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm capitalize">{p.category}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {p.special_tag && <Badge className="rounded-full text-[10px] bg-primary/10 text-primary border-primary/20 w-fit">{p.special_tag}</Badge>}
                        {p.discount_percentage > 0 && <Badge variant="outline" className="rounded-full text-[10px] w-fit">{p.discount_percentage}% off</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {p.original_price > 0 && <span className="text-xs text-muted-foreground line-through mr-1">{'\u20B9'}{p.original_price}</span>}
                      {'\u20B9'}{p.price_per_kg}
                    </TableCell>
                    <TableCell>{p.stock} kg</TableCell>
                    <TableCell className="text-sm">{p.linked_farmers_count || 0}</TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[p.status] || ''} border-0 rounded-full text-xs capitalize`}>{p.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/product/${p.product_id}`} target="_blank"><Button variant="ghost" size="icon" title="Preview"><Eye className="h-3.5 w-3.5" /></Button></Link>
                        <Link to={`/admin/products/${p.product_id}/edit`}><Button data-testid={`edit-product-${p.product_id}`} variant="ghost" size="icon"><Edit2 className="h-3.5 w-3.5" /></Button></Link>
                        <Button data-testid={`toggle-product-${p.product_id}`} variant="ghost" size="icon" onClick={() => toggleStatus(p)} title={p.status === 'active' ? 'Draft' : 'Publish'}>
                          {p.status === 'active' ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </Button>
                        <Button data-testid={`delete-product-${p.product_id}`} variant="ghost" size="icon" onClick={() => remove(p)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
